import React, { lazy, Suspense, useRef, useState } from 'react';
import { ExperienceItem, ProjectItem } from './ExperienceItem';
import ShaderBackground from './ShaderBackground';
import './App.css';

// loaded on first hover so the globe costs nothing until it's wanted
const GlobeCard = lazy(() => import('./GlobeCard'));

// only where there's hover and room beside the column for the card
const GLOBE_QUERY = '(hover: hover) and (pointer: fine) and (min-width: 1100px)';

const SCOTIA_PLAZA = { name: 'scotia plaza · toronto', lat: 43.6489, lon: -79.3797, city: 'toronto' };
const EY_TOWER = { name: 'ey tower · 100 adelaide st w', lat: 43.6497, lon: -79.3825, city: 'toronto' };
const ONTARIO_TECH = { name: 'ontario tech · oshawa, on', lat: 43.9456, lon: -78.8968, city: 'oshawa' };
const LARPHACKS = { name: 'larphacks · st. george campus', lat: 43.6629, lon: -79.3957, city: 'stgeorge' };
const UTSC = { name: 'hack the valley · utsc', lat: 43.7845, lon: -79.187, city: 'utsc' };

const experience = [
  {
    logoSrc: '/Scotiabank_logo.jpg',
    title: 'software engineer intern',
    company: 'scotiabank',
    link: 'https://www.scotiabank.com/',
    description: 'risk warehouse airflow pipelines, python + oracle + unix',
    term: 'f26 (Present)',
    place: SCOTIA_PLAZA,
  },
  {
    logoSrc: '/SportsForce_logo.jpg',
    title: 'software engineer intern',
    company: 'sportsforce',
    link: 'https://www.sportsforceonline.com/',
    description: 'athlete–coach recruiting app - 35+ features across 51 merged prs, flutter + typescript',
    term: 's26 (Present)',
    place: { name: 'san diego, ca', lat: 32.7157, lon: -117.1611, city: 'sandiego' },
  },
  {
    logoSrc: '/Riipen_logo.png',
    title: 'software engineer',
    company: 'riipen',
    link: 'https://www.riipen.com/',
    description: 'full-stack web features on azure, c# (.net) + react + sql server',
    term: 'f25–w26',
    place: { name: 'remote', remote: true },
  },
  {
    logoSrc: '/Scotiabank_logo.jpg',
    title: 'software developer intern',
    company: 'scotiabank',
    link: 'https://www.scotiabank.com/',
    description: 'treasury - python automation cutting manual data handling 60%, lcr + nsfr validation',
    term: 'f25',
    place: SCOTIA_PLAZA,
  },
  {
    logoSrc: '/EY_logo.png',
    title: 'software engineer intern',
    company: 'ernst & young',
    link: 'https://www.ey.com/',
    description: 'snowflake native app + data pipelines, react + typescript + snowpark',
    term: 's25',
    place: EY_TOWER,
  },
];

const projects = [
  {
    title: 'whispr',
    place: LARPHACKS,
    description: 'ai earpiece that whispers what to say in real time',
    badge: 'hackathon winner',
    links: [
      { label: 'github', href: 'https://github.com/JulianCruzet/whispr' },
      { label: 'demo', href: 'https://www.youtube.com/watch?v=WMQa3FV8oWw' },
    ],
  },
  {
    title: 'recoup',
    place: UTSC,
    description: 'ai crm that finds unclaimed benefits & pre-fills the paperwork',
    badge: 'hackathon winner',
    links: [
      { label: 'github', href: 'https://github.com/JulianCruzet/recoup' },
      { label: 'demo', href: 'https://www.youtube.com/watch?v=5I_BiABXUfg' },
      { label: 'live', href: 'https://www.recoup.finance' },
    ],
  },
  {
    title: 'orpheus',
    place: ONTARIO_TECH,
    description: 'autonomous ai agent for end-to-end shopify workflows',
    badge: 'hackathon winner',
    links: [{ label: 'github', href: 'https://github.com/JulianCruzet/Orpheus' },
            { label: 'demo', href: 'https://www.youtube.com/watch?v=LDryhyT2_u4' },
    ],
  },
];

const socials = [
  { label: 'email', href: 'mailto:ali.shamsii@hotmail.com' },
  { label: 'linkedin', href: 'https://linkedin.com/in/alishamsi1' },
  { label: 'github', href: 'https://github.com/shambo2' },
];

// OTU webring widget (https://github.com/VinceKLW/otu-webring) — prev/next
// resolve against this site's hostname on otu-ring.com.
function Webring() {
  const host = encodeURIComponent(window.location.hostname.replace(/^www\./, ''));
  return (
    <div className="webring">
      <a href={`https://otu-ring.com/prev.html?from=${host}`} title="Previous site">←</a>
      <a href="https://otu-ring.com" target="_blank" rel="noopener noreferrer" title="OTU Webring">
        <img src="https://otu-ring.com/assets/ontariotech.svg" alt="OTU Webring" />
      </a>
      <a href={`https://otu-ring.com/next.html?from=${host}`} title="Next site">→</a>
    </div>
  );
}

function App() {
  const pageRef = useRef(null);
  const hideTimer = useRef(0);
  const [globe, setGlobe] = useState({ place: null, top: 0, visible: false });

  function showPlace(place, row) {
    if (!place || !window.matchMedia(GLOBE_QUERY).matches) return;
    clearTimeout(hideTimer.current);
    const pageTop = pageRef.current.getBoundingClientRect().top;
    const rowRect = row.getBoundingClientRect();
    // centre on the row, but keep the whole card inside the viewport
    const half = 140;
    const centre = Math.min(
      Math.max(rowRect.top + rowRect.height / 2, half + 16),
      window.innerHeight - half - 16
    );
    setGlobe({ place, top: centre - pageTop, visible: true });
  }

  function hidePlace() {
    // short grace period so sliding between rows doesn't flicker the card
    clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => setGlobe((g) => ({ ...g, visible: false })), 140);
  }

  const hoverProps = (place) => ({
    onMouseEnter: (e) => showPlace(place, e.currentTarget),
    onMouseLeave: hidePlace,
  });

  return (
    <>
      <ShaderBackground />
      <main className="page" ref={pageRef}>
        <header className="intro">
          <h1>ali shamsi</h1>
          <p className="tagline">cs @ ontario tech university</p>
        </header>

        <section>
          <h2 className="label"><span className="num">01</span> work</h2>
          {experience.map((item, i) => (
            <ExperienceItem key={item.company + item.term} index={i + 1} {...item} {...hoverProps(item.place)} />
          ))}
        </section>

        <section>
          <h2 className="label"><span className="num">02</span> projects</h2>
          {projects.map((item, i) => (
            <ProjectItem key={item.title} index={experience.length + i + 1} {...item} {...hoverProps(item.place)} />
          ))}
        </section>

        <footer>
          <nav className="socials">
            {socials.map(({ label, href }) => (
              <a key={label} href={href} target="_blank" rel="noopener noreferrer">{label}</a>
            ))}
          </nav>
          <Webring />
        </footer>

        {globe.place && (
          <Suspense fallback={null}>
            <GlobeCard {...globe} />
          </Suspense>
        )}
      </main>
    </>
  );
}

export default App;
